import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Row, Col, Alert, Spinner, Badge } from 'react-bootstrap';

// Helper to build API base depending on environment
const buildApi = (path) => {
  const base = import.meta.env.VITE_API_BASE || 'http://localhost:5000';
  return `${base}${path}`;
};

const TestingScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [context, setContext] = useState({ leagueId: null, draftId: null });

  const isAuthorized = userInfo?.email === 'ethan@leaguerunner.net';

  const addLog = (entry) => setLog((prev) => [{ ts: new Date().toISOString(), ...entry }, ...prev]);

  const withAuthHeaders = () => ({
    'Content-Type': 'application/json',
  });

  const createLeague = async () => {
    setBusy(true);
    addLog({ level: 'info', msg: 'Creating test league…' });
    try {
      const res = await fetch(buildApi('/api/leagues'), {
        method: 'POST',
        headers: withAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          name: `Test League ${new Date().toLocaleString()}`,
          description: 'Automated test league',
          isPrivate: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to create league');
      setContext((c) => ({ ...c, leagueId: data._id }));
      addLog({ level: 'success', msg: 'League created', data });
    } catch (e) {
      addLog({ level: 'error', msg: e.message });
    } finally {
      setBusy(false);
    }
  };

  const createDraft = async () => {
    if (!context.leagueId) return addLog({ level: 'warn', msg: 'No leagueId in context. Create a league first.' });
    setBusy(true);
    addLog({ level: 'info', msg: 'Creating draft…', ctx: context });
    try {
      const res = await fetch(buildApi('/api/draft'), {
        method: 'POST',
        headers: withAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          leagueId: context.leagueId,
          rounds: 10,
          draftMethod: 'snake',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to create draft');
      setContext((c) => ({ ...c, draftId: data._id }));
      addLog({ level: 'success', msg: 'Draft created', data });
    } catch (e) {
      addLog({ level: 'error', msg: e.message });
    } finally {
      setBusy(false);
    }
  };

  const executeDraft = async () => {
    if (!context.draftId) return addLog({ level: 'warn', msg: 'No draftId in context. Create a draft first.' });
    setBusy(true);
    addLog({ level: 'info', msg: 'Executing draft…', ctx: context });
    try {
      const res = await fetch(buildApi(`/api/testing/execute-draft/${context.draftId}`), {
        method: 'POST',
        headers: withAuthHeaders(),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to execute draft');
      addLog({ level: 'success', msg: 'Draft executed', data });
    } catch (e) {
      addLog({ level: 'error', msg: e.message });
    } finally {
      setBusy(false);
    }
  };

  const calculateScores = async () => {
    if (!context.leagueId) return addLog({ level: 'warn', msg: 'No leagueId in context. Create a league first.' });
    setBusy(true);
    addLog({ level: 'info', msg: 'Calculating scores…', ctx: context });
    try {
      const res = await fetch(buildApi(`/api/testing/recalculate-scores/${context.leagueId}`), {
        method: 'POST',
        headers: withAuthHeaders(),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed to calculate scores');
      addLog({ level: 'success', msg: 'Scores calculated', data });
    } catch (e) {
      addLog({ level: 'error', msg: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!userInfo) {
    return (
      <Row className="mt-4">
        <Col md={8} lg={6} xl={5} className="mx-auto">
          <Alert variant="warning">You must be signed in to access Testing.</Alert>
          <Button variant="primary" onClick={() => navigate('/login')}>Sign in</Button>
        </Col>
      </Row>
    );
  }

  if (!isAuthorized) {
    return (
      <Row className="mt-4">
        <Col md={8} lg={6} xl={5} className="mx-auto">
          <Alert variant="danger">Not authorized. This Testing section is visible to ethan@leaguerunner.net only.</Alert>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="mt-4">
      <Col md={8} lg={6} xl={5} className="mx-auto">
        <Card className="modern-card">
          <div className="modern-card-header">
            <h4 className="modern-card-title">Testing Section</h4>
            <p className="modern-card-subtitle">Run end-to-end flows safely</p>
          </div>
          <Card.Body>
            <div className="d-flex gap-2 flex-wrap mb-3">
              <Button disabled={busy} onClick={createLeague} variant="primary">
                {busy ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                Create League
              </Button>
              <Button disabled={busy} onClick={createDraft} variant="secondary">Create Draft</Button>
              <Button disabled={busy} onClick={executeDraft} variant="warning">Execute Draft</Button>
              <Button disabled={busy} onClick={calculateScores} variant="success">Calculate Scores</Button>
            </div>
            <div className="border-top pt-3">
              <h6 className="mb-2">Activity Log</h6>
              {log.length === 0 ? (
                <p className="text-muted mb-0">No activity yet.</p>
              ) : (
                <div>
                  {log.map((entry, i) => (
                    <div key={i} className="mb-2">
                      <Badge bg={entry.level === 'success' ? 'success' : entry.level === 'error' ? 'danger' : entry.level === 'warn' ? 'warning' : 'info'} className="me-2 text-uppercase">{entry.level}</Badge>
                      <small className="text-muted me-2">{new Date(entry.ts).toLocaleString()}</small>
                      <span>{entry.msg}</span>
                      {entry.data ? (
                        <pre className="mt-1 bg-light p-2 rounded" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(entry.data, null, 2)}</pre>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default TestingScreen;
